import type { SimpleChanges, TemplateRef } from '@angular/core';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { DynamicLocaleService } from '../dynamic-locale.service';
import { InterpolatedBlueprintService } from '../interpolated-blueprint.service';
import {
  BlueprintBase,
  CoverBlueprint,
  FullTemplateInfo,
  GroupBase,
  InterpolatedBlueprint,
  Layout,
} from '../models/blueprint.model';
import {
  DataObject,
  RecordUpdateEmission,
  ReportInfo,
  ReportOptions,
  ReportView,
  TemplateProjectionMap,
} from '../models/report.model';
import {
  PropertyGroup,
  ReportAllRecordsImage,
  TemplateField,
  TemplateFieldsCatalog,
  ViewTemplateCatalog,
} from '../models/template.model';
import { ReportBluePrintInterpolationService } from '../report-blueprint-interpolation.service';
import { ReportExportViewService } from '../report-export-view.service';
import { ReportImagesService } from '../report-images.service';
import { ReportViews, TEMPLATE_ASSOCIATIONS } from '../shared/constants/template.const';
import {
  SelectTemplateDialogComponent,
  SelectTemplateDialogInputData,
} from '../shared/select-template-dialog/select-template-dialog.component';
import { NotificationService } from '../shared/services/notification.service';
import { TemplateDynamicService } from '../template-dynamic.service';
import { ReportResetImagesService } from '../views/presentation-dynamic-view/record-body/record-image/report-reset-images.service';

@UntilDestroy()
@Component({
  template: '',
  standalone: false,
})
export class AbstractDynamicReportViewComponent implements OnChanges, OnInit, OnDestroy {
  @HostBinding('attr.loaded') loadedStatus = 'loading';

  @Input() reportData: ReportInfo;
  @Input() currentView: ReportView;
  @Input() reportLocale: number;
  @Input() columns: { [entity: string]: { [columnField: string]: any } }; // TODO(ppt): add type
  /** @deprecated Use {@link templateProjectionMap} instead. */
  @Input() confidentialityTmpl: TemplateRef<any>;
  /** Map of TemplateRef to be used on the view. */
  @Input() templateProjectionMap: TemplateProjectionMap;
  @Input() reportOptions: ReportOptions;
  @Input() isPDF = false;
  @Input() customConfigs: { symbols: DataObject };
  @Input() isViewHidden: boolean;
  @Input() isUKFeaturesEnabled: boolean;
  @Output() reportUpdateClick: EventEmitter<RecordUpdateEmission> = new EventEmitter<RecordUpdateEmission>();
  @Output() templateIdChange: EventEmitter<ReportView> = new EventEmitter<ReportView>();

  templateFieldsCatalog: ViewTemplateCatalog;
  headerGroups: GroupBase<TemplateField>[];
  bodyGroups: GroupBase<TemplateField>[];
  footerGroups: GroupBase<TemplateField>[];
  contactGroups: GroupBase<TemplateField>[];
  imageGroups: GroupBase<TemplateField>[];

  propertyGroups: PropertyGroup[];

  private _templateId: Subject<number> = new Subject<number>();
  private templatePopupOpen = false;
  set templateId(value: number) {
    this._templateId.next(value);
  }
  get templateId$() {
    return this._templateId.asObservable();
  }

  constructor(
    protected reportExportViewService: ReportExportViewService,
    protected interpolatedBlueprintService: InterpolatedBlueprintService,
    private notificationService: NotificationService,
    private reportImagesService: ReportImagesService,
    private localeService: DynamicLocaleService,
    private templateDynamicService: TemplateDynamicService,
    private reportResetImagesService: ReportResetImagesService,
    private reportBluePrintInterpolationService: ReportBluePrintInterpolationService
  ) {}

  ngOnChanges({ columns, currentView, reportLocale, reportData }: SimpleChanges) {
    function hasNewRecord() {
      if (reportData == null) {
        return false;
      }
      const { previousValue: prevReportData, currentValue: currReportData } = reportData;
      return prevReportData.records.length != currReportData.records.length;
    }
    if (
      (!!columns || !!currentView || reportLocale != null || hasNewRecord()) &&
      !!this.columns &&
      !!this.currentView &&
      this.reportLocale !== null
    ) {
      this.initializeDynamicReport();
      this.fetchAdditionalImagesOfReport();
      if (
        this.reportData &&
        this.reportData.originalViews &&
        this.reportData.originalViews.some(a => a.id === ReportViews.comparison) &&
        (this.currentView.id === ReportViews.comparison || this.currentView.id === ReportViews.presentation)
      ) {
        this.fetchPropertyGroupsOfReport(this.currentView.id);
      }
      this.localeService.setReportLocaleId(this.reportLocale);
    }
  }

  ngOnInit() {
    this.initInterpolationSubscription();
  }

  ngOnDestroy(): void {
    // required by angular-l10n
  }

  public onTemplateSelected(selectedTemplateId: number) {
    const oldTemplateId = this.currentView.templateId;
    this.currentView = { ...this.currentView, templateId: selectedTemplateId };
    const { catalogUri, templateId } = this.currentView;
    this.triggerBlueprintInterpolation(catalogUri, templateId);
    if (oldTemplateId !== selectedTemplateId) {
      this.templateIdChange.emit({ ...this.currentView });
    }
  }

  public onTemplateSelectedLater(templateAndViewAndSelectedId) {
    const { selectedTemplateId, viewsAndTemplates } = templateAndViewAndSelectedId;
    const oldTemplateId = this.currentView.templateId;
    this.currentView = { ...this.currentView, templateId: selectedTemplateId };
    if (oldTemplateId !== selectedTemplateId) {
      this.templateIdChange.emit({ ...viewsAndTemplates });
    }
  }

  private fetchTemplateAndViewToSend(selectedTemplateId: number) {
    this.templateDynamicService.setTemplateId(this.currentView.id, selectedTemplateId);
    const viewsAndTemplates = this.templateDynamicService.setViewandTemplate(
      this.reportLocale,
      this.currentView,
      selectedTemplateId
    );
    if (viewsAndTemplates.length) {
      const templateAndViewAndSelectedId = { selectedTemplateId, viewsAndTemplates };
      viewsAndTemplates.forEach((item, _index) => {
        this.templateDynamicService.saveAssociatedTemplates(this.reportData, item).subscribe(() => {
          this.onTemplateSelectedLater(templateAndViewAndSelectedId);
        });
      });
    }
  }

  protected prepareGroupsObjectToDisplay(
    interpolatedBlueprint: InterpolatedBlueprint,
    contactsInterpolatedBlueprint?: InterpolatedBlueprint
  ) {
    const groupsData = this.interpolatedBlueprintService.flattenBlueprintColumnsToGroups(interpolatedBlueprint);
    /*
     * Returns the groups which are only needed in HTML view. @{filterOutInHtml}
     * is boolean value, added in template blue print json
     */
    const groups = groupsData.filter(group => !group.filterOutInHtml);
    this.headerGroups = groups.filter(group => group.htmlHeader);
    this.bodyGroups = groups.filter(
      group => !group.htmlHeader && !group.htmlFooter && !group.contacts && !group.hasImages
    );
    this.footerGroups = groups.filter(group => group.htmlFooter);
    this.imageGroups = groups.filter(group => group.hasImages);
    if (contactsInterpolatedBlueprint) {
      const contactsGroupsData =
        this.interpolatedBlueprintService.flattenBlueprintColumnsToGroups(contactsInterpolatedBlueprint);
      this.contactGroups = contactsGroupsData.filter(group => !group.filterOutInHtml);
    }
  }

  private initInterpolationSubscription() {
    this.templateId$
      .pipe(
        untilDestroyed(this),
        switchMap(templateId => {
          const mainLayoutInter$ = this.interpolatedBlueprintService
            .fetchInterpolationByTemplateId$(templateId, Layout.MainLayout)
            .pipe(take(1));
          const contactsLayoutInter$ = this.interpolatedBlueprintService
            .fetchInterpolationByTemplateId$(templateId, Layout.Contacts)
            .pipe(take(1));
          return forkJoin([mainLayoutInter$, contactsLayoutInter$]);
        })
      )
      .subscribe(([interpolatedBlueprint, contactsInterpolatedBlueprint]) => {
        this.prepareGroupsObjectToDisplay(interpolatedBlueprint, contactsInterpolatedBlueprint);
        this.loadedStatus = 'true';
      });
  }

  private initializeDynamicReport() {
    const { catalogUri, id } = this.currentView;
    let { templateId } = this.currentView;
    // find if reportData views has templateId
    if (!templateId) {
      const selectedReportView = this.reportData.views.find(item => item.id === this.currentView.id);
      templateId = selectedReportView ? selectedReportView.templateId : null;
    }
    if (TEMPLATE_ASSOCIATIONS[this.reportLocale]) {
      const templateInfo = this.templateDynamicService.getTemplates(this.currentView);
      if (templateId) {
        this.triggerBlueprintInterpolation(catalogUri, templateId);
      }
      templateInfo.subscribe(templates => {
        this.templateFieldsCatalog = templates;
        if (TEMPLATE_ASSOCIATIONS[this.reportLocale][0] === id) {
          let observableResult$: Observable<number> = of(templateId);
          if (templateId == null) {
            observableResult$ = this.templateDynamicService.templateDialog(this.reportData.reportId, id);
            observableResult$.pipe(take(1)).subscribe(selectedTemplateId => {
              this.templateDynamicService.setTemplateId(id, selectedTemplateId);
              this.fetchTemplateAndViewToSend(selectedTemplateId);
              this.reportResetImagesService
                .setReportAdditionalImages(this.reportData.records, this.reportData.reportId, selectedTemplateId)
                .subscribe(() => {
                  this.fetchAdditionalImagesOfReport();
                });
            });
          } else {
            this.templateDynamicService.setTemplateId(id, templateId);
            this.onTemplateSelected(templateId);
          }
        }
      });
    } else {
      const initializationFlow: Observable<number> = this.triggerTemplateCatalogRetrieval(catalogUri, templateId);
      initializationFlow.pipe(take(1)).subscribe(selectedTemplateId => {
        this.templatePopupOpen = false;
        this.onTemplateSelected(selectedTemplateId);
      });
    }
  }

  private triggerTemplateCatalogRetrieval(catalogUri: string, templateId: number): Observable<number> {
    return this.reportExportViewService.getViewTemplateFieldsCatalog(catalogUri).pipe(
      switchMap(templateFieldsCatalog => {
        this.templateFieldsCatalog = templateFieldsCatalog;
        const dialogInputData: SelectTemplateDialogInputData = {
          reportId: this.reportData.reportId,
          templates: templateFieldsCatalog,
          viewId: this.currentView.id,
        };
        let observableResult$: Observable<number> = of(templateId);
        if (templateId == null) {
          if (this.isViewHidden || templateFieldsCatalog.length === 1) {
            // if the view only contains a single template or the view is hidden, we select and save
            // it by default.
            const selectedTemplate = templateFieldsCatalog[0];
            const saveTemplate$ = this.reportExportViewService.saveTemplate(
              this.reportData.reportId,
              this.currentView.id,
              selectedTemplate
            );
            observableResult$ = saveTemplate$.pipe(map(() => selectedTemplate.templateId));
          } else {
            if (this.templatePopupOpen) {
              return;
            }
            this.templatePopupOpen = true;
            observableResult$ = this.notificationService
              .openDialog<number>(SelectTemplateDialogComponent, dialogInputData)
              .pipe(filter(selectedTemplate => selectedTemplate != null));
          }
        }
        return observableResult$;
      })
    );
  }

  private triggerBlueprintInterpolation(catalogUri: string, templateId: number) {
    this.reportBluePrintInterpolationService
      .blueprintInterpolation(catalogUri, templateId, this.reportData.entity, this.reportLocale, this.columns)
      .subscribe(templateId => (this.templateId = templateId));
  }

  /**
   * Get and map all the data needed for the report, including the cover,
   * the blueprint and template fields data.
   *
   * @param partialTemplateInfo
   */
  private getFullTemplateInfo(
    partialTemplateInfo: [BlueprintBase, TemplateFieldsCatalog]
  ): Observable<FullTemplateInfo> {
    const [blueprint, templateCatalog] = partialTemplateInfo;
    let coverSlideInfo$: Observable<CoverBlueprint> = of(null);
    // verify that cover path exists
    if (blueprint.coverPath != null) {
      coverSlideInfo$ = this.reportExportViewService.getCoverPageBluePrint(blueprint.coverPath);
    }
    return coverSlideInfo$.pipe(
      // map to FullTemplateInfo
      map(coverSlideInfo => {
        return { coverSlideInfo, blueprint, templateCatalog };
      })
    );
  }

  /**
   * this method is used to fetch the additional image id of the report.
   */
  private fetchAdditionalImagesOfReport() {
    this.reportExportViewService.getImages(this.reportData.reportId).subscribe((res: ReportAllRecordsImage) => {
      this.reportImagesService.setReportRecordImages(res);
    });
  }

  /**
   * this method is used to fetch the property groups of the report.
   */
  private fetchPropertyGroupsOfReport(viewId: number) {
    this.reportExportViewService
      .getPropertyGroups(this.reportData.reportId, viewId)
      .subscribe((res: PropertyGroup[]) => {
        this.interpolatedBlueprintService.setReportPropertyGroups(res);
      });
    this.interpolatedBlueprintService.getReportPropertyGroupsStream().subscribe(res => (this.propertyGroups = res));
  }
}
